/**
 * Runtime bridge for @ezhire/admin-features.
 *
 * The package is consumed by two apps with slightly different runtime
 * shapes (apps/web is the live tenant app; apps/admin will inherit
 * these features in Phase 5 once routes migrate). Rather than duplicate
 * the Supabase client, auth store, and module-store query bindings into
 * each app, the consuming app calls `configureAdminFeatures()` once at
 * startup with its own implementations. Admin code inside this package
 * imports the helpers below — they delegate to the configured runtime
 * at call time, NOT at module-load time, so consumers can defer
 * configuration until after their own initialization.
 *
 * The cross-feature deps from module-store (access-request queue, all
 * templates, decide / upsert / delete mutations) cannot just be moved
 * into this package — they are also consumed by tenant-side UI
 * (notifications-popover, members.access-requests, request-access
 * -dialog). Tenant code shouldn't have to import from an admin package
 * to reach those, so they stay in module-store and the runtime peer
 * -injects them.
 *
 * The bridge passes HOOKS (not raw queryOptions objects) for these
 * reads. Wrapping each query in a hook fences off TanStack's QueryKey
 * variance — function-arg variance makes `UseQueryOptions<T,..,readonly
 * unknown[]>` reject a concrete `string[]` queryKey, but `() =>
 * UseQueryResult<T>` has no such pitfall. The wiring file in apps/web
 * just defines `() => useQuery(options)` once.
 *
 * Only the bridge slots admin code actually uses live here. Other
 * module-store / templates helpers (e.g. fillReasonTemplate,
 * activeTemplatesQueryOptions) belong to tenant code and are NOT
 * funneled through this package.
 */

import type { Database } from '@ezhire/types';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type { SupabaseClient, User } from '@supabase/supabase-js';

export type RoleTier = 'super_admin' | 'admin' | 'editor' | 'viewer';

/**
 * Minimal auth-store shape admin code reads from. Both apps' real
 * zustand stores are supersets — they satisfy this structurally.
 * Today the only field admin code reads is `user`; keep the surface
 * small so future bridge additions are deliberate.
 */
export interface AdminAuthState {
  user: User | null;
}

type ModuleAccessRequestRow =
  Database['public']['Tables']['module_access_requests']['Row'];
type ProfileRow = Database['public']['Tables']['profiles']['Row'];
type ModuleRow = Database['public']['Tables']['modules']['Row'];
type ModuleFeatureRow = Database['public']['Tables']['module_features']['Row'];

export type AccessRequestRow = ModuleAccessRequestRow;

/**
 * Mirrors `AccessRequestWithContext` in
 * apps/web/src/features/module-store/queries.ts — declared here so
 * admin code that consumes the queue gets full property typing without
 * importing back across the package boundary. Both definitions share
 * the same Database row shapes, so drift is structural-only.
 */
export interface AccessRequestWithContext extends ModuleAccessRequestRow {
  user: ProfileRow | null;
  module: ModuleRow | null;
  feature: ModuleFeatureRow | null;
}

export type AccessRequestTemplate =
  Database['public']['Tables']['access_request_templates']['Row'];

/**
 * Runtime shape of `required_fields` (typed as Json in the DB). Mirrored
 * from `apps/web/src/features/module-store/templates.ts` so admin code's
 * templates panel can narrow the Json column at the use site.
 */
export type TemplateField = {
  key: string;
  label: string;
  kind?: 'text' | 'textarea' | 'select';
  required?: boolean;
  placeholder?: string;
  options?: string[];
};

export type DecideAccessRequestInput = {
  request_id: string;
  decision: 'approved' | 'denied';
  note?: string;
  approver_id: string;
};

export type DecideAccessRequestMutation = UseMutationResult<
  unknown,
  Error,
  DecideAccessRequestInput,
  unknown
>;

export type UpsertTemplateInput = Partial<AccessRequestTemplate> & {
  module_id: string;
  name: string;
};

export type UpsertTemplateMutation = UseMutationResult<
  unknown,
  Error,
  UpsertTemplateInput,
  unknown
>;

export type DeleteTemplateMutation = UseMutationResult<
  unknown,
  Error,
  string,
  unknown
>;

/**
 * The full contract the consuming app must provide. Keep this minimal
 * — every addition is a new coupling point between admin-features and
 * the consuming app.
 */
export interface AdminFeaturesRuntime {
  getSupabase(): SupabaseClient<Database> | null;
  requireSupabase(): SupabaseClient<Database>;
  useAuthStore<T>(selector: (state: AdminAuthState) => T): T;
  useIsSuperAdmin(): boolean;
  useAccessRequestsQueue(): UseQueryResult<AccessRequestWithContext[], Error>;
  useDecideAccessRequestMutation(): DecideAccessRequestMutation;
  useAllTemplates(): UseQueryResult<AccessRequestTemplate[], Error>;
  useUpsertTemplateMutation(): UpsertTemplateMutation;
  useDeleteTemplateMutation(): DeleteTemplateMutation;
}

let runtime: AdminFeaturesRuntime | null = null;

export function configureAdminFeatures(impl: AdminFeaturesRuntime): void {
  if (runtime && runtime !== impl) {
    // Second call from a different impl: HMR re-execution of the wiring
    // file, a misconfigured bundler that pulled in both apps' wiring
    // into one chunk, or a test that re-mounted the runtime. Warn so
    // it's visible, but allow it (HMR is the common case in dev and
    // throwing would break the inner loop).
    // eslint-disable-next-line no-console
    console.warn(
      '@ezhire/admin-features: configureAdminFeatures() called more than once. Second call wins.',
    );
  }
  runtime = impl;
}

export function getAdminFeaturesRuntime(): AdminFeaturesRuntime {
  if (!runtime) {
    throw new Error(
      '@ezhire/admin-features: configureAdminFeatures() must be called at app startup before any admin feature is rendered.',
    );
  }
  return runtime;
}

// ─── Convenience accessors used by admin code (formerly `@/lib/supabase`, `@/stores/auth-store`, `@/features/module-store/*`) ──

export function getSupabase(): SupabaseClient<Database> | null {
  return getAdminFeaturesRuntime().getSupabase();
}

export function requireSupabase(): SupabaseClient<Database> {
  return getAdminFeaturesRuntime().requireSupabase();
}

export function useAuthStore<T>(selector: (state: AdminAuthState) => T): T {
  return getAdminFeaturesRuntime().useAuthStore(selector);
}

export function useIsSuperAdmin(): boolean {
  return getAdminFeaturesRuntime().useIsSuperAdmin();
}

export function useAccessRequestsQueue(): UseQueryResult<
  AccessRequestWithContext[],
  Error
> {
  return getAdminFeaturesRuntime().useAccessRequestsQueue();
}

export function useDecideAccessRequestMutation(): DecideAccessRequestMutation {
  return getAdminFeaturesRuntime().useDecideAccessRequestMutation();
}

export function useAllTemplates(): UseQueryResult<
  AccessRequestTemplate[],
  Error
> {
  return getAdminFeaturesRuntime().useAllTemplates();
}

export function useUpsertTemplateMutation(): UpsertTemplateMutation {
  return getAdminFeaturesRuntime().useUpsertTemplateMutation();
}

export function useDeleteTemplateMutation(): DeleteTemplateMutation {
  return getAdminFeaturesRuntime().useDeleteTemplateMutation();
}
